import React from 'react';
import { format } from 'date-fns';
import { EstimateExportData, getFinalLineItemAmount, getFinalLineItemUnitCost } from '@/utils/estimateExportUtils';
import { ChangeOrderExportData } from '@/utils/changeOrderExportUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/lib/utils';
import { getUnitLabel } from '@/types/unit-types';
import ChangeOrderPreview from './ChangeOrderPreview';

interface EstimatePreviewProps {
  data: EstimateExportData | ChangeOrderExportData;
  className?: string;
  pdfCompatible?: boolean;
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'draft':
      return <Badge variant="outline">Draft</Badge>;
    case 'sent':
      return <Badge variant="secondary">Sent</Badge>;
    case 'accepted':
      return <Badge variant="default" className="bg-green-600">Accepted</Badge>;
    case 'rejected':
      return <Badge variant="destructive">Rejected</Badge>;
    case 'expired':
      return <Badge variant="destructive">Expired</Badge>;
    case 'cancelled':
      return <Badge variant="destructive">Cancelled</Badge>;
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
};

const EstimatePreview: React.FC<EstimatePreviewProps> = ({ 
  data, 
  className = '',
  pdfCompatible = false 
}) => {
  // Route change orders to their own preview
  if ('isChangeOrder' in data && data.isChangeOrder) {
    return (
      <ChangeOrderPreview 
        data={data as ChangeOrderExportData}
        className={className}
        pdfCompatible={pdfCompatible}
      />
    );
  }

  const { estimate, customer, company, categorizedLineItems, subtotal, discountAmount, discountPercentage, taxAmount, totalAmount } = data as EstimateExportData;

  const isVatRegistered = company?.is_vat_registered ?? false;
  const companyVatRate = company?.["VAT Rate"] || 15;
  const showVat = isVatRegistered && companyVatRate > 0;

  // Flatten all line items from categories
  const allLineItems = categorizedLineItems.flatMap(cat => cat.lineItems);

  // Helper to build address lines
  const getFormattedAddress = (entity: any) => {
    if (!entity) return [];
    const lines = [];
    if (entity.street_address) lines.push(entity.street_address);
    if (entity.suburb) lines.push(entity.suburb);
    if (entity.city) lines.push(entity.city);
    if (entity.postal_code) lines.push(entity.postal_code);
    return lines;
  };

  // Customer helpers
  const isCommercialCustomer = customer?.customer_type === 'commercial';

  const getCustomerDisplayName = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_name) return customer.business_name;
    return `${customer.name} ${customer.last_name}`;
  };

  const getCustomerEmail = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_email) return customer.business_email;
    return customer.emails?.length > 0 ? customer.emails[0] : null;
  };

  const getCustomerPhone = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_phone) return customer.business_phone;
    return customer.cell_numbers?.length > 0 ? customer.cell_numbers[0] : null;
  };

  return (
    <div className={`bg-white text-black min-h-full p-8 pb-16 flex flex-col gap-10 max-w-5xl mx-auto ${className}`} id="estimate-regular-preview">
      {/* Header: Estimate number, status, dates */}
      <div className="flex flex-wrap justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="text-2xl font-bold text-primary">
            Estimate {estimate.estimate_number || 'EST-PENDING'}
          </div>
          {getStatusBadge(estimate.status)}
        </div>
        <div className="text-right space-y-1">
          <div className="text-gray-600 text-xs">
            Date: {format(new Date(estimate.estimate_date || estimate.created_at), 'dd MMMM yyyy')}
          </div>
          {estimate.expires_at && (
            <div className="text-gray-600 text-xs">
              Valid Until: {format(new Date(estimate.expires_at), 'dd MMMM yyyy')}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column */}
        <div className="md:col-span-1 flex flex-col gap-4">
          {/* Company Info */}
          <Card className="mb-3">
            <CardHeader>
              <CardTitle className="text-primary text-lg">Company Info</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              {company?.name && <div className="font-semibold">{company.name}</div>}
              {getFormattedAddress(company).length > 0 && (
                <div className="text-sm text-gray-700">
                  {getFormattedAddress(company).map((line: string, i: number) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              )}
              {company?.is_vat_registered && company?.vat_number && (
                <div className="text-sm text-gray-600">VAT No: {company.vat_number}</div>
              )}
              {company?.email && <div className="text-sm">{company.email}</div>}
              {company?.company_phone_number && <div className="text-sm">{company.company_phone_number}</div>}
            </CardContent>
          </Card>

          {/* Client Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-blue-800 text-lg">Client</CardTitle>
            </CardHeader>
            <CardContent className="pb-2">
              {customer ? (
                <>
                  <div className="font-semibold">{getCustomerDisplayName()}</div>
                  {getFormattedAddress(customer).length > 0 && (
                    <div className="text-sm text-gray-700">
                      {getFormattedAddress(customer).map((line: string, i: number) => (
                        <div key={i}>{line}</div>
                      ))}
                    </div>
                  )}
                  {isCommercialCustomer && customer.vat_number && (
                    <div className="text-sm text-gray-600">VAT Reg No.: {customer.vat_number}</div>
                  )}
                  {getCustomerPhone() && <div className="text-sm">{getCustomerPhone()}</div>}
                  {getCustomerEmail() && <div className="text-sm">{getCustomerEmail()}</div>}
                </>
              ) : (
                <div className="text-gray-500 text-sm">No customer assigned</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="md:col-span-2 flex flex-col gap-6">
          {/* Estimate Details */}
          <Card className="mb-2">
            <CardHeader>
              <CardTitle>{estimate.title}</CardTitle>
            </CardHeader>
            <CardContent>
              {estimate.description && <p className="mb-2">{estimate.description}</p>}
              {estimate.special_instructions && (
                <div className="text-xs text-muted-foreground mb-1">
                  <span className="font-bold">Special Instructions: </span>{estimate.special_instructions}
                </div>
              )}
              {estimate.payment_terms && (
                <div className="text-xs text-muted-foreground mb-1">
                  <span className="font-bold">Payment Terms: </span>{estimate.payment_terms}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Financial Summary */}
          <Card>
            <CardHeader>
              <CardTitle>Financial Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>{formatCurrency(subtotal)}</span>
              </div>
              {(discountAmount ?? 0) > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span>Discount{discountPercentage ? ` (${discountPercentage}%)` : ''}:</span>
                  <span>-{formatCurrency(discountAmount || 0)}</span>
                </div>
              )}
              {showVat && (
                <div className="flex justify-between">
                  <span>VAT ({companyVatRate}%):</span>
                  <span>{formatCurrency(taxAmount)}</span>
                </div>
              )}
              <div className="border-t pt-2 mt-2">
                <div className="flex justify-between font-bold text-lg">
                  <span>Total:</span>
                  <span>{formatCurrency(totalAmount)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle>Estimate Items</CardTitle>
            </CardHeader>
            <CardContent className="pb-6">
              {allLineItems.length > 0 ? (
                <>
                  {/* Mobile card view - hidden in PDF mode */}
                  {!pdfCompatible && (
                    <div className="sm:hidden space-y-3">
                      {allLineItems.map((item: any, idx: number) => (
                        <div key={item.id || idx} className="border rounded-md p-3 space-y-2">
                          <div className="font-medium text-sm whitespace-normal break-words">
                            {item.manual_description || item.description}
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                            <div>Unit: <span className="text-foreground">{getUnitLabel(item.base_unit)}</span></div>
                            <div>Qty: <span className="text-foreground">{item.quantity_in_base_units ?? item.quantity ?? 0}</span></div>
                            <div>Unit Cost: <span className="text-foreground font-mono">{formatCurrency(getFinalLineItemUnitCost(item))}</span></div>
                            <div>Total: <span className="text-foreground font-mono font-semibold">{formatCurrency(getFinalLineItemAmount(item))}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Desktop table view - no responsive wrapper in PDF mode */}
                  <div className={pdfCompatible ? "overflow-x-auto" : "hidden sm:block overflow-x-auto"}>
                    <table className="table-auto w-full text-sm border">
                      <thead>
                        <tr>
                          <th className="border px-2 py-1">Description</th>
                          <th className="border px-2 py-1">Unit</th>
                          <th className="border px-2 py-1">Quantity</th>
                          <th className="border px-2 py-1">Unit Cost (R)</th>
                          <th className="border px-2 py-1">Total (R)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allLineItems.map((item: any, idx: number) => (
                          <tr key={item.id || idx}>
                            <td className="border px-2 py-1">{item.manual_description || item.description}</td>
                            <td className="border px-2 py-1">{getUnitLabel(item.base_unit)}</td>
                            <td className="border px-2 py-1">{item.quantity_in_base_units ?? item.quantity ?? 0}</td>
                            <td className="border px-2 py-1">{formatCurrency(getFinalLineItemUnitCost(item)).replace('R', '')}</td>
                            <td className="border px-2 py-1">{formatCurrency(getFinalLineItemAmount(item)).replace('R', '')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground">No estimate items found.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EstimatePreview;
